import React from 'react';
import { getFilterColumn, setFilterColumn } from '../../../../store/utils';
import DualListBox, { DualListBoxItem } from '../../../DualListBox';
import useStore from '../../../Hooks/useStore';
import Input from '../../../Input';
import * as S from './Styles';
import SelectedItemMask from './SelectedItemMask';
import AvailableItemMask from './AvailableItemMask';
import Form from '../..';
import { useSnackbar } from 'notistack';

const BannerBatch = () => {
  const [getters, setters] = useStore();
  const {
    product: { getProduct },
    form: { getForm },
    user: { getSelectedFilial },
  } = getters;
  const {
    form: { setForm },
    product: {
      setProduct,
      findFilteredProducts,
      findFilteredPromotionalProducts,
      createSingleFormsForProducts,
      autocompletePromotionalProducts,
    },
  } = setters;
  const { enqueueSnackbar } = useSnackbar();

  const searchFilter = getForm('batchBanner.filters.products');

  const totalOfItems = getForm('batchBanner.batch.total');
  const setTotalOfItems = (total: number) =>
    setForm('batchBanner.batch.total', total);

  const type = getForm('batchBanner.batch.productType');
  const setProductType = (type: 'normal' | 'promotion') =>
    setForm('batchBanner.batch.productType', type as any);

  const setSearchFilter = (filter: SearchFilter) =>
    setForm('batchBanner.filters.products', filter as any);

  const productListFilter = getProduct('filters.promotion.filter');

  const setProductListFilter = (filter: SearchFilter) =>
    setProduct({
      field: 'filters.promotion.filter',
      value: filter,
    });

  const setProductFilter = (value: string) => {
    let filter = { ...searchFilter };
    filter = setFilterColumn('ean', value, filter, 'LIKE', 'OR');
    filter = setFilterColumn('code', value, filter, 'LIKE', 'OR');
    filter = setFilterColumn('resume', value, filter, 'LIKE', 'OR');
    setSearchFilter(filter);
  };
  const productFilter = getFilterColumn('resume', searchFilter);

  const onProductListHandler = () => {
    (() => {
      switch (type) {
        case 'promotion':
          return findFilteredPromotionalProducts;
        default:
          return findFilteredProducts;
      }
    })()(searchFilter).then((products) => {
      setForm(
        'batchBanner.batch.availableProducts',
        products.data.map((product) => ({
          ...product,
          type,
          identifier: -1,
        })),
      );
      setTotalOfItems(products.meta.total);
    });
  };

  const selectedFilial = getSelectedFilial();
  let productTimerId: NodeJS.Timeout;
  React.useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(productTimerId);
      productTimerId = setTimeout(() => {
        onProductListHandler();
      }, 700);
    };
    handleInputChange();
    return () => {
      clearTimeout(productTimerId);
    };
  }, [searchFilter, selectedFilial, type]);

  const selectedProducts = getForm('batchBanner.singleForms').map(
    ({ product: { product } }) => product,
  );

  const availableProducts = getForm(
    'batchBanner.batch.availableProducts',
  ).filter(
    ({ id, type, promotionalProductId }) =>
      !selectedProducts.find(
        (item) =>
          id === item.id &&
          type === item.type &&
          promotionalProductId === item.promotionalProductId,
      ),
  );

  const availableItemsList = availableProducts.map(({ ...product }, index) => ({
    ...product,
    type,
    identifier: index + 1,
  }));

  const handleSelectItems = (items: DualListBoxItem[]) => {
    const newProducts = availableProducts.filter(
      ({ id, type, promotionalProductId }) =>
        items.find(
          (item) =>
            id === item.id &&
            type === item.type &&
            promotionalProductId === item.promotionalProductId,
        ),
    );

    const newSelectedItems = [...newProducts, ...selectedProducts];

    if (type === 'normal') {
      const newSelectedItemIds = newSelectedItems
        .filter(({ type: tp }) => tp === 'normal')
        .map(({ id }) => id);

      setSearchFilter(
        setFilterColumn(
          'id',
          newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
          searchFilter,
          'NOTIN',
          'AND',
        ),
      );
    }

    if (type === 'promotion') {
      const newSelectedItemIds = newSelectedItems
        .filter(({ type: tp }) => tp === 'promotion')
        .map(({ promotionalProductId }) => promotionalProductId);

      setSearchFilter(
        setFilterColumn(
          'promotionalProductId',
          newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
          searchFilter,
          'NOTIN',
          'AND',
        ),
      );

      setProductListFilter(
        setFilterColumn(
          'promotionalProductId',
          newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
          productListFilter,
          'NOTIN',
          'AND',
        ),
      );
    }

    createSingleFormsForProducts(newProducts);
  };

  const handleRemoveItems = (
    items?: DualListBoxItem<FilteredProductItem>[],
  ) => {
    if (!items) {
      setSearchFilter(setFilterColumn('id', '', searchFilter, 'NOTIN', 'AND'));
      setSearchFilter(
        setFilterColumn(
          'promotionalProductId',
          '',
          searchFilter,
          'NOTIN',
          'AND',
        ),
      );
      setProductListFilter(
        setFilterColumn(
          'promotionalProductId',
          '',
          productListFilter,
          'NOTIN',
          'AND',
        ),
      );
      setForm('batchBanner.singleForms', []);
      setForm('batchBanner.batch.pinnedSingleForms', []);
      setForm('batchBanner.showSingleFormId', 0);
      return;
    }

    const newSelectedItems = selectedProducts.filter(
      ({ identifier }) => !items.find((item) => identifier === item.identifier),
    );

    if (type === 'normal') {
      const newSelectedItemIds = newSelectedItems
        .filter(({ type: tp }) => tp === 'normal')
        .map(({ id }) => id);

      setSearchFilter(
        setFilterColumn(
          'id',
          newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
          searchFilter,
          'NOTIN',
          'AND',
        ),
      );
    }

    if (type === 'promotion') {
      const newSelectedItemIds = newSelectedItems
        .filter(({ type: tp }) => tp === 'promotion')
        .map(({ promotionalProductId }) => promotionalProductId);

      setSearchFilter(
        setFilterColumn(
          'promotionalProductId',
          newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
          searchFilter,
          'NOTIN',
          'AND',
        ),
      );

      setProductListFilter(
        setFilterColumn(
          'promotionalProductId',
          newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
          productListFilter,
          'NOTIN',
          'AND',
        ),
      );
    }

    const pinnedSingleForms = getForm('batchBanner.batch.pinnedSingleForms');
    const newPinnedSingleForms = pinnedSingleForms.filter(
      (identifier) => !items.find((item) => identifier === item.identifier),
    );
    setForm('batchBanner.batch.pinnedSingleForms', newPinnedSingleForms);

    const singleForms = getForm('batchBanner.singleForms');
    const newSingleForms = singleForms.filter(
      ({ id }) => !items.find((item) => id === item.identifier),
    );
    setForm('batchBanner.singleForms', newSingleForms);

    const showSingleFormId = getForm('batchBanner.showSingleFormId');

    const containsShowSingleFormId =
      items.findIndex(({ identifier }) => showSingleFormId === identifier) !==
      -1;

    if (containsShowSingleFormId)
      setForm(
        'batchBanner.showSingleFormId',
        newPinnedSingleForms[0] ?? newSingleForms[0]?.id ?? 0,
      );
  };

  const getItemsPerPage = () =>
    getForm('batchBanner.filters.products.limit') ?? 0;
  const getPage = () => getForm('batchBanner.filters.products.page') ?? 0;

  const setPage = (page: number) => {
    setForm('batchBanner.filters.products.page', page);
  };

  const setItemsPerPage = (itemsPerPage: number) => {
    setForm('batchBanner.filters.products.limit', itemsPerPage);
  };

  const [autocompleteTypeLoading, setAutocompleteTypeLoading] =
    React.useState(false);
  const [autocompletePromotionLoading, setAutocompletePromotionLoading] =
    React.useState(false);

  const [typeListFocused, setTypeListFocused] = React.useState(false);
  const [promotionListFocused, setPromotionListFocused] = React.useState(false);

  const typeFilter = getForm('batchBanner.autocompletes.type.value');
  const promotionFilter = getForm('batchBanner.autocompletes.promotion.value');

  const setTypeFilter = (e) => {
    const value = e.target.value;
    setForm('batchBanner.autocompletes.type.value', value);
    setSearchFilter(
      setFilterColumn('type', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  const promotionAutocompleteFilter = getForm(
    `batchBanner.autocompletes.promotion.filter`,
  );

  const setPromotionAutocompleteFilter = (filter?: Filter[]) =>
    setForm(`batchBanner.autocompletes.promotion.filter`, filter);

  const setType = (value: string) => {
    setForm('batchBanner.autocompletes.type.value', value);
    setSearchFilter(
      setFilterColumn('type', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  const handleAutocompletePromotionTypeFilter = () => {
    setAutocompleteTypeLoading(true);
    autocompletePromotionalProducts(getForm('batchBanner.autocompletes.type'))
      .then((types) => {
        setForm(
          `batchBanner.autocompletes.items.type`,
          types.map((name, id) => ({ id, name })),
        );
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar as promoções', {
          variant: 'error',
        });
      })
      .finally(() => {
        setAutocompleteTypeLoading(false);
      });
  };

  let typeTimerId: NodeJS.Timeout;
  React.useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(typeTimerId);
      typeTimerId = setTimeout(() => {
        if (typeListFocused) handleAutocompletePromotionTypeFilter();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(typeTimerId);
    };
  }, [typeFilter, typeListFocused]);

  let promotionTimerId: NodeJS.Timeout;

  const handleAutocompletePromotionFilter = () => {
    setAutocompletePromotionLoading(true);
    autocompletePromotionalProducts(
      getForm('batchBanner.autocompletes.promotion'),
    )
      .then((promotions) => {
        setForm(
          `batchBanner.autocompletes.items.promotion`,
          promotions.map((name, id) => ({ id, name })),
        );
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar as promoções', {
          variant: 'error',
        });
      })
      .finally(() => {
        setAutocompletePromotionLoading(false);
      });
  };

  React.useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(promotionTimerId);
      promotionTimerId = setTimeout(() => {
        if (promotionListFocused) handleAutocompletePromotionFilter();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(promotionTimerId);
    };
  }, [promotionFilter, promotionListFocused]);

  const setPromotionFilter = (e) => {
    const value = e.target.value;
    setForm('batchBanner.autocompletes.promotion.value', value);
    setSearchFilter(
      setFilterColumn('promotion', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  const setPromotion = (value: string) => {
    setForm('batchBanner.autocompletes.promotion.value', value);
    setSearchFilter(
      setFilterColumn('promotion', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  return (
    <S.TabContainer>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
        }}
      >
        <Input
          label={'Código do Produto'}
          value={productFilter as string}
          onChange={(e) => {
            setProductFilter(e.target.value);
          }}
          errorMessage={false}
        />
        <Input.Checkbox
          style={{ margin: 0 }}
          className="form-banner-field"
          errorMessage={false}
          checked={type === 'promotion'}
          onChange={(e) => {
            const type = e.target.checked ? 'promotion' : 'normal';
            setProductType(type);
            if (type === 'normal') {
              setForm('batchBanner.autocompletes.type.value', '');
              setForm('batchBanner.autocompletes.promotion.value', '');
              let filter = setFilterColumn(
                'type',
                '',
                searchFilter,
                'LIKE',
                'AND',
              );
              filter = setFilterColumn('promotion', '', filter, 'LIKE', 'AND');
              const newSelectedItemIds = selectedProducts
                .filter(({ type: tp }) => tp === 'normal')
                .map(({ id }) => id);
              filter = setFilterColumn(
                'id',
                newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
                filter,
                'NOTIN',
                'AND',
              );
              filter = setFilterColumn(
                'promotionalProductId',
                '',
                filter,
                'NOTIN',
                'AND',
              );
              setSearchFilter(filter);
            }

            if (e.target.value === 'promotion') {
              const newSelectedItemIds = selectedProducts
                .filter(({ type: tp }) => tp === 'promotion')
                .map(({ promotionalProductId }) => promotionalProductId);

              let filter = setFilterColumn(
                'promotionalProductId',
                newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
                searchFilter,
                'NOTIN',
                'AND',
              );
              filter = setFilterColumn('id', '', filter, 'NOTIN', 'AND');
              setSearchFilter(filter);
            }
          }}
          label="Preços Promocionais"
        />
        {type === 'promotion' ? (
          <>
            <Form.Input.Dropdown
              className="form-banner-field"
              label="Tipo da promoção"
              errorMessage={false}
              hasClearItem
              items={getForm('batchBanner.autocompletes.items.type')}
              value={typeFilter}
              loading={autocompleteTypeLoading}
              onChange={(e) => {
                setTypeFilter(e);
                setPromotionAutocompleteFilter(
                  setFilterColumn(
                    'type',
                    e.target.value,
                    { filter: promotionAutocompleteFilter },
                    'LIKE',
                    'AND',
                  ).filter,
                );
                setSearchFilter(
                  setFilterColumn(
                    'type',
                    e.target.value,
                    searchFilter,
                    'LIKE',
                    'AND',
                  ),
                );
              }}
              onFocus={() => {
                setTypeListFocused(true);
                setType('');
                setPromotionAutocompleteFilter(
                  setFilterColumn(
                    'type',
                    '',
                    { filter: promotionAutocompleteFilter },
                    'LIKE',
                    'AND',
                  ).filter,
                );
                setSearchFilter(
                  setFilterColumn('type', '', searchFilter, 'LIKE', 'AND'),
                );
              }}
              onBlur={() => {
                setTypeListFocused(false);
              }}
              onSelected={(item) => {
                setTypeListFocused(false);
                setType(`${item.name}`);
                setPromotionAutocompleteFilter(
                  setFilterColumn(
                    'type',
                    `${item.name}`,
                    { filter: promotionAutocompleteFilter },
                    'LIKE',
                    'AND',
                  ).filter,
                );
                setSearchFilter(
                  setFilterColumn(
                    'type',
                    `${item.name}`,
                    searchFilter,
                    'LIKE',
                    'AND',
                  ),
                );
              }}
            />
            <Form.Input.Dropdown
              className="form-banner-field"
              label="Promoção"
              errorMessage={false}
              items={getForm('batchBanner.autocompletes.items.promotion')}
              hasClearItem
              value={promotionFilter}
              loading={autocompletePromotionLoading}
              onChange={(e) => {
                setPromotionFilter(e);
              }}
              onFocus={() => {
                setPromotionListFocused(true);
                setPromotion('');
              }}
              onBlur={() => {
                setPromotionListFocused(false);
              }}
              onSelected={(item) => {
                setPromotionListFocused(false);
                setPromotion(`${item.name}`);
              }}
            />
          </>
        ) : null}
      </div>

      <DualListBox
        availableItems={availableItemsList}
        selectedItems={selectedProducts}
        onSelectItems={handleSelectItems}
        onRemoveItems={handleRemoveItems}
        currentPage={getPage()}
        itemsPerPage={getItemsPerPage()}
        totalOfItems={totalOfItems}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
        AvailableItemMask={AvailableItemMask}
        SelectedItemMask={SelectedItemMask}
      />
    </S.TabContainer>
  );
};

export default BannerBatch;
